import axios from 'axios';
import { cacheService } from './cacheService';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const getValidToken = () => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (payload.exp && payload.exp <= Math.floor(Date.now() / 1000)) {
      localStorage.removeItem('token');
      return null;
    }
    return token;
  } catch {
    localStorage.removeItem('token');
    return null;
  }
};

export const api = axios.create({
  baseURL: API_URL,
  timeout: 10000, // Reduced from default
});

// Cache configuration by endpoint (path pattern -> TTL in milliseconds)
const CACHE_CONFIG = {
  '/portfolio': 20 * 1000, // 20s
  '/trading/portfolio': 20 * 1000,
  '/risk/metrics': 30 * 1000, // 30s
  '/risk/limits': 60 * 1000, // 60s
  '/trading/asset-class-status': 60 * 1000,
  '/analytics': 30 * 1000,
  '/strategies': 60 * 1000,
  '/trades': 10 * 1000, // 10s for active trades
  '/opportunities': 5 * 1000, // 5s for opportunities (real-time)
  // Default for other GET requests
  'default': 5 * 60 * 1000, // 5 minutes
};

// Request interceptor: add token + check cache
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Only cache GET requests
  if (config.method === 'get') {
    const cacheKey = `${config.method}:${config.url}:${JSON.stringify(config.params || {})}`;
    config.cacheKey = cacheKey;

  }

  return config;
});

// Response interceptor: cache responses + handle errors
api.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    if (response.config.method === 'get' && response.config.cacheKey) {
      const endpoint = response.config.url.split('?')[0];
      let ttl = CACHE_CONFIG.default;

      // Find matching cache config
      for (const [pattern, cacheTTL] of Object.entries(CACHE_CONFIG)) {
        if (pattern !== 'default' && endpoint.includes(pattern)) {
          ttl = cacheTTL;
          break;
        }
      }

      cacheService.set(response.config.cacheKey, response.data, ttl);
    }

    return response;
  },
  (error) => {
    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      cacheService.clear(); // Clear cache on logout
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export default api;